import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { MapContainer, Polygon, TileLayer, ZoomControl } from 'react-leaflet';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import * as d3 from 'd3';
import MapResetButton from 'src/shared/components/map-reset-button/MapResetButton';
import config from 'src/config';
import ReactLeafletCustomControl from 'src/shared/components/react-leaflet-custom-control/ReactLeafletCustomControl';
import { swapCoordinateLatLng } from 'src/shared/helpers/coordinateHelpers';
import { Site } from 'src/shared/types';
import googleMapStyles from 'src/shared/data/googleMapStyles';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import ExploreButton from 'src/shared/components/explore-button/ExploreButton';
import useSiteSummaryAnalytics from 'src/app/projects/views/site-summary/hooks/useSiteSummaryAnalytics';
import styles from './HabitatMiniMap.module.scss';
import useHabitatMiniMap from '../../hooks/useHabitatMiniMap';
import 'leaflet/dist/leaflet.css';
import 'src/shared/styles/leaflet.css';

const MIN_ZOOM_LEVEL = 5;
const MAX_ZOOM_LEVEL = 15;

const HabitatMiniMap = () => {
    const mapRef = useRef<L.Map>(null);
    const { filteredSites, activeLayerUrls, getColourForSite } = useHabitatMiniMap();
    const [bounds, setBounds] = useState<L.LatLngBounds | undefined>();
    const [center, setCenter] = useState<[number, number] | undefined>();
    const navigate = useAppNavigation();
    const { trackExploreButtonClick } = useSiteSummaryAnalytics();

    // Mini map uses its own local state via useHabitatMiniMap and ignores global habitat filters

    useEffect(() => {
        if (!filteredSites.length) {
            return;
        }
        const allCoordinates = filteredSites.flatMap(entry => entry.geometry.coordinates.flatMap(entry => entry));
        if (!allCoordinates.length) {
            return;
        }
        const polygon = L.polygon(allCoordinates.map((coord: [number, number]) => [coord[1], coord[0]]));
        const polygonBounds = polygon.getBounds();

        if (mapRef.current) {
            mapRef.current.fitBounds(polygonBounds);
            setBounds(polygonBounds);
            setCenter(d3.polygonCentroid(filteredSites[0].geometry.coordinates[0]));
            if (mapRef.current.getZoom() < MIN_ZOOM_LEVEL) {
                mapRef.current.setZoom(MIN_ZOOM_LEVEL);
            }
            if (mapRef.current.getZoom() > MAX_ZOOM_LEVEL) {
                mapRef.current.setZoom(MAX_ZOOM_LEVEL);
            }
        }
    }, [filteredSites]);

    return (
        <div className={styles.container}>
            <MapContainer
                ref={mapRef}
                center={center}
                scrollWheelZoom={false}
                zoomControl={false}
                maxZoom={19}
                zoom={0}
                attributionControl={false}
                style={{ width: '100%', height: '100%' }}
            >
                <ReactLeafletGoogleLayer
                    apiKey={config.googleMapsJavascriptApiKey}
                    useGoogMapsLoader={true}
                    {...({ styles: googleMapStyles } as any)}
                />

                {/* Polygons */}
                {mapRef.current &&
                    filteredSites.map((site: Site, siteIndex: number) => {
                        const borderPathOptions = {
                            color: getColourForSite(site.properties.name || ''),
                            fillOpacity: 0,
                            weight: 2,
                        } as L.PathOptions;
                        const fillPathOptions = {
                            color: getColourForSite(site.properties.name || ''),
                            fillOpacity: 0.3,
                            weight: 0,
                        } as L.PathOptions;

                        return site.geometry.coordinates.map(coordinate => {
                            const transformedCoodrinates = [...coordinate].map(swapCoordinateLatLng);
                            return (
                                <React.Fragment key={siteIndex}>
                                    <Polygon positions={transformedCoodrinates as L.LatLngExpression[]} pathOptions={fillPathOptions} />
                                    <Polygon positions={transformedCoodrinates as L.LatLngExpression[]} pathOptions={borderPathOptions} />
                                </React.Fragment>
                            );
                        });
                    })}

                {activeLayerUrls.length > 0 && activeLayerUrls.map(url => <TileLayer key={url} url={`${url}tiles/{z}/{x}/{y}.png`} />)}

                <div className={styles.exploreBtnContainer}>
                    <ExploreButton
                        buttonText='Open habitat map'
                        onClick={() => {
                            trackExploreButtonClick({ section: 'habitat_map' });
                            navigate.toHabitatMap();
                        }}
                        className={styles.exploreBtn}
                    />
                </div>

                <ZoomControl position='bottomright' />
                <ReactLeafletCustomControl position='bottomright'>
                    <MapResetButton onClick={() => (bounds ? mapRef.current?.fitBounds(bounds) : null)} />
                </ReactLeafletCustomControl>
            </MapContainer>
        </div>
    );
};

export default HabitatMiniMap;
